import { useState, type FormEvent } from "react";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

const FORM_ENDPOINT = "https://readdy.ai/api/form/db73j8f799gd4v2febbg";
const MESSAGE_LIMIT = 500;

const contactCards = [
  {
    icon: "ri-mail-line",
    title: "电子邮箱",
    lines: ["support@papavideo.com", "business@papavideo.com"],
  },
  {
    icon: "ri-phone-line",
    title: "客服热线",
    lines: ["400-880-1234", "周一至周五 9:00 - 18:00"],
  },
  {
    icon: "ri-map-pin-line",
    title: "办公地址",
    lines: ["北京市朝阳区视频大道 88 号", "啪帕大厦 12 层"],
  },
  {
    icon: "ri-time-line",
    title: "服务时间",
    lines: ["在线反馈 24 小时受理", "邮件回复一般 1 个工作日内"],
  },
];

type Status = "idle" | "submitting" | "success" | "error";

export default function ContactPage() {
  useDocumentTitle(
    "联系我们 - 啪啪视频",
    "通过在线反馈表单或客服邮箱、热线联系啪啪视频团队，我们会在第一时间回复你的建议与合作咨询。",
    "联系啪啪视频,啪啪视频客服,在线反馈,合作咨询,视频平台",
  );

  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");
  const [messageLength, setMessageLength] = useState(0);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("contact_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setMessageLength(0);
      form.reset();
      return;
    }
    formData.delete("contact_alt");

    setStatus("submitting");
    setFormError("");

    const body = new URLSearchParams();
    formData.forEach((value, key) => {
      body.append(key, String(value));
    });

    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText) as Record<string, unknown>;
      } catch {
        parsed = null;
      }

      const meta = parsed?.meta as Record<string, unknown> | undefined;
      const code = typeof parsed?.code === "string" ? parsed.code : "";
      const serverMessage =
        (typeof meta?.message === "string" && meta.message) ||
        (typeof parsed?.message === "string" && parsed.message) ||
        (typeof meta?.detail === "string" && meta.detail) ||
        responseText;

      const isSpam = /spam/i.test(String(serverMessage));

      if (!response.ok || code !== "OK" || isSpam) {
        setStatus("error");
        setFormError(
          serverMessage && !isSpam
            ? String(serverMessage)
            : "提交没有成功，请稍后重试，或直接发送邮件到 support@papavideo.com。",
        );
        return;
      }

      setStatus("success");
      setMessageLength(0);
      form.reset();
    } catch {
      setStatus("error");
      setFormError("网络连接异常，提交失败，请检查网络后重试。");
    }
  };

  return (
    <div className="w-full pb-16">
      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-14 md:px-6 md:py-16">
          <span className="inline-flex items-center gap-2 rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-800">
            <i className="ri-customer-service-2-line"></i>
            联系我们
          </span>
          <h1 className="mt-5 font-heading text-3xl font-bold leading-tight text-foreground-950 md:text-4xl">
            有任何想法，都欢迎告诉我们
          </h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            内容建议、使用反馈或者合作意向，都可以通过下面的表单提交。我们会认真阅读每一条留言，并尽快与你取得联系。
          </p>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1280px] px-4 py-12 md:px-6">
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {contactCards.map((card) => (
            <div key={card.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
              <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
                <i className={`${card.icon} text-xl`}></i>
              </span>
              <h2 className="mt-4 font-heading text-base font-semibold text-foreground-900">{card.title}</h2>
              <div className="mt-2 flex flex-col gap-1">
                {card.lines.map((line) => (
                  <p key={line} className="text-sm text-foreground-500">
                    {line}
                  </p>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 grid gap-8 lg:grid-cols-5">
          <div className="lg:col-span-3">
            <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
              <h2 className="font-heading text-xl font-bold text-foreground-950">在线反馈</h2>
              <p className="mt-1.5 text-sm text-foreground-500">填写下面的信息，我们会在 1 个工作日内回复。</p>

              {status === "success" ? (
                <div className="mt-6 flex flex-col items-center rounded-lg border border-secondary-200 bg-secondary-50 px-6 py-12 text-center">
                  <span className="flex h-16 w-16 items-center justify-center rounded-full bg-secondary-500 text-background-50">
                    <i className="ri-check-line text-3xl"></i>
                  </span>
                  <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">提交成功，感谢你的留言！</h3>
                  <p className="mt-2 max-w-sm text-sm leading-relaxed text-foreground-500">
                    我们已经收到你的反馈，会尽快安排小伙伴与你联系。也可以继续提交新的留言。
                  </p>
                  <button
                    type="button"
                    onClick={() => setStatus("idle")}
                    className="mt-6 cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors duration-200 hover:border-primary-300 hover:text-primary-600"
                  >
                    继续留言
                  </button>
                </div>
              ) : (
                <form
                  id="pp-contact-form"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="mt-6 flex flex-col gap-5"
                  noValidate={false}
                >
                  <div className="grid gap-5 sm:grid-cols-2">
                    <div className="flex flex-col gap-2">
                      <label htmlFor="contact-name" className="text-sm font-medium text-foreground-800">
                        你的称呼
                      </label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        required
                        placeholder="请输入你的名字"
                        className="rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-800 outline-none transition-colors duration-200 placeholder:text-foreground-400 focus:border-primary-400"
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <label htmlFor="contact-email" className="text-sm font-medium text-foreground-800">
                        电子邮箱
                      </label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        required
                        placeholder="name@example.com"
                        className="rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-800 outline-none transition-colors duration-200 placeholder:text-foreground-400 focus:border-primary-400"
                      />
                    </div>
                  </div>

                  <div className="flex flex-col gap-2">
                    <label htmlFor="contact-message" className="text-sm font-medium text-foreground-800">
                      留言内容
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      rows={5}
                      maxLength={MESSAGE_LIMIT}
                      onChange={(event) => setMessageLength(event.target.value.length)}
                      placeholder="写下你的建议、问题或合作意向（不超过 500 字）"
                      className="resize-none rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm leading-relaxed text-foreground-800 outline-none transition-colors duration-200 placeholder:text-foreground-400 focus:border-primary-400"
                    ></textarea>
                    <p className="self-end text-xs text-foreground-400">
                      {messageLength} / {MESSAGE_LIMIT}
                    </p>
                  </div>

                  <input
                    type="text"
                    name="contact_alt"
                    className="assist-extra-offset"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />

                  {status === "error" && formError ? (
                    <div className="flex items-start gap-2 rounded-md border border-accent-200 bg-accent-50 px-4 py-3 text-sm text-accent-800">
                      <i className="ri-error-warning-line mt-0.5 text-base"></i>
                      <span>{formError}</span>
                    </div>
                  ) : null}

                  <button
                    type="submit"
                    disabled={status === "submitting"}
                    className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors duration-200 hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <i className={status === "submitting" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"}></i>
                    {status === "submitting" ? "正在提交..." : "提交留言"}
                  </button>
                </form>
              )}
            </div>
          </div>

          <aside className="lg:col-span-2">
            <div className="flex h-full flex-col gap-5">
              <div className="rounded-lg border border-background-200 bg-background-50 p-6">
                <h2 className="font-heading text-lg font-bold text-foreground-950">为什么选择联系我们</h2>
                <ul className="mt-4 flex flex-col gap-3 text-sm text-foreground-600">
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-line mt-0.5 text-secondary-600"></i>
                    每条留言都会由真人阅读，不做模板化回复。
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-line mt-0.5 text-secondary-600"></i>
                    内容建议一经采纳，会在后续版本中更新。
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-line mt-0.5 text-secondary-600"></i>
                    商务合作请留下联系方式，我们会主动沟通。
                  </li>
                </ul>
              </div>

              <div className="relative flex-1 overflow-hidden rounded-lg">
                <img
                  src="https://readdy.ai/api/search-image?query=cozy%20modern%20customer%20support%20desk%20with%20headset%20and%20warm%20lighting%20in%20a%20bright%20office%2C%20clean%20minimal%20aesthetic%2C%20ultra%20detailed%2C%20professional%20editorial%20photography&width=800&height=600&seq=pp-contact-01&orientation=landscape"
                  alt="啪啪视频客服团队在线为你解答"
                  title="联系啪啪视频 - 在线反馈与咨询"
                  className="h-full w-full object-cover object-top"
                />
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}